'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { memo, type ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { boolean, type InferType, object, string } from 'yup';

import { Auth } from '@air/api';
import { Checkbox } from '@air/primitive-checkbox';
import { useToasts } from '@air/provider-toast';
import { getProviderNameFromSSOOptions } from '@air/utils-auth';
import { reportErrorToBugsnag } from '@air/utils-error';

import { Button, type ButtonVariants } from '~/components/Button';
import { Input } from '~/components/Input';
import { Label } from '~/components/Label';
import { accountSSOOptionsFetcher } from '~/fetchers/accountSSOOptionsFetcher';
import { getAccountSSOOptionsKey } from '~/hooks/api/useAccountSSOOptions';

const SAML_SSO_LAST_USED_EMAIL_KEY = 'saml-sso-last-used-email';

export const SAMLFormValidationSchema = object({
  email: string().email().required(),
  rememberEmail: boolean().default(false),
});

export type SAMLFormInput = InferType<typeof SAMLFormValidationSchema>;

export type SAMLFormProps = {
  /** Default email to pre-fill the form */
  defaultEmail?: string;
  /** Content to render below the form (e.g. a back link) */
  footer?: ReactNode;
  /** Callback to navigate to OAuth waiting page with the generated id */
  onNavigateToOAuth: (id: string) => void;
  /** Callback to navigate to provider selection page with the email */
  onNavigateToProviders: (email: string) => void;
  /** Submit button size — defaults to 'large' for backwards compatibility */
  size?: ButtonVariants['size'];
};

export const SAMLForm = memo(
  ({ defaultEmail = '', footer, onNavigateToOAuth, onNavigateToProviders, size = 'large' }: SAMLFormProps) => {
    const formId = useId();
    const [lastUsedEmail, setLastUsedEmail] = useState('');

    useEffect(() => {
      const stored = localStorage.getItem(SAML_SSO_LAST_USED_EMAIL_KEY);

      if (stored) {
        setLastUsedEmail(stored);
      }
    }, []);

    const {
      control,
      formState: { isSubmitting, isValid },
      handleSubmit,
      register,
      reset,
    } = useForm({
      defaultValues: { email: defaultEmail || lastUsedEmail, rememberEmail: !!lastUsedEmail },
      resolver: yupResolver(SAMLFormValidationSchema),
    });

    useEffect(() => {
      if (lastUsedEmail && !defaultEmail) {
        reset({ email: lastUsedEmail, rememberEmail: true });
      }
    }, [defaultEmail, lastUsedEmail, reset]);

    const { showToast } = useToasts();
    const queryClient = useQueryClient();

    const onSubmit = useCallback(
      async ({ email, rememberEmail }: SAMLFormInput) => {
        if (rememberEmail) {
          localStorage.setItem(SAML_SSO_LAST_USED_EMAIL_KEY, email);
        } else {
          localStorage.removeItem(SAML_SSO_LAST_USED_EMAIL_KEY);
        }

        try {
          const data = await queryClient.fetchQuery({
            queryFn: () => accountSSOOptionsFetcher({ email }),
            queryKey: getAccountSSOOptionsKey({ email }),
          });

          if (data.length === 0) {
            showToast('SSO is not enabled for this email address');
          } else if (data.length > 1) {
            onNavigateToProviders(email);
          } else {
            const ssoOption = data[0];

            try {
              const samlProvider = getProviderNameFromSSOOptions(ssoOption);
              const id = uuidv4();

              if (samlProvider) {
                Auth.ssoSamlSignin(samlProvider, { id });

                onNavigateToOAuth(id);
              } else {
                throw new Error('Missing provider for saml sign in!');
              }
            } catch (error) {
              reportErrorToBugsnag({
                context: 'Failed login user using saml sso',
                error,
                metadata: { ssoOption },
              });

              showToast('An error occurred. Please try again later');
            }
          }
        } catch {
          showToast('An error occurred. Please try again later');
        }
      },
      [onNavigateToOAuth, onNavigateToProviders, queryClient, showToast],
    );

    return (
      <>
        <form className="flex w-full flex-col gap-6" id={formId} onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${formId}-email`}>Work email</Label>
            <Input autoFocus id={`${formId}-email`} placeholder="Add your email" type="email" {...register('email')} />
          </div>

          <Controller
            control={control}
            name="rememberEmail"
            render={({ field }) => (
              <Label className="flex cursor-pointer items-center gap-2" htmlFor={`${formId}-remember`}>
                <Checkbox
                  checked={field.value}
                  id={`${formId}-remember`}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                />
                Remember email on this device
              </Label>
            )}
          />

          <Button
            appearance="primary"
            disabled={!isValid || isSubmitting}
            isLoading={isSubmitting}
            size={size}
            type="submit"
          >
            Log in
          </Button>
        </form>

        {footer}
      </>
    );
  },
);

SAMLForm.displayName = 'SAMLForm';
